import React, { useState, useRef, useEffect } from 'react';
import { Zap, Clock, MoreHorizontal, FileText, Users, Trash2 } from 'lucide-react';

/**
 * WorkflowCard - Displays an automation in the library list
 * 
 * Expects Firestore automation document shape:
 * {
 *   id: string,
 *   name: string,
 *   status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'STOPPED',
 *   trigger: { type: string, config: object },
 *   steps: array,
 *   createdAt: timestamp,
 *   updatedAt: timestamp
 * }
 */
const WorkflowCard = ({ workflow, onCardClick, onStatusToggle, onDeleteAutomation, isMobile }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);
  // Derive display values from Firestore data
  const isActive = workflow.status === 'ACTIVE';
  const triggerType = workflow.trigger?.type || 'No trigger';
  const stepCount = Array.isArray(workflow.steps) ? workflow.steps.length : 0;
  const leadCount = Number.isFinite(Number(workflow.leadCount)) ? Number(workflow.leadCount) : 0;
  const completedCount = Number.isFinite(Number(workflow.completedCount)) ? Number(workflow.completedCount) : 0;

  // Format the last edited time
  const formatLastEdited = (timestamp) => {
    if (!timestamp) return 'Never';

    // Handle Firestore timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    if (onStatusToggle) {
      onStatusToggle(workflow.id, !isActive);
    }
  };

  // Status badge component
  const StatusBadge = () => {
    const statusConfig = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
      PAUSED: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Paused' },
      STOPPED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Stopped' },
      DRAFT: { bg: 'bg-stone-100', text: 'text-stone-600', label: 'Draft' },
    };
    const config = statusConfig[workflow.status] || statusConfig.DRAFT;

    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (isMobile) {
    return (
      <div
        onClick={() => onCardClick(workflow)}
        className="bg-white border border-notion-border rounded-xl p-4 shadow-card active:scale-[0.98] transition-transform cursor-pointer"
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500 border border-orange-100 flex-shrink-0">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-notion-text">{workflow.name || 'Untitled Automation'}</h3>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge />
                <span className="text-[10px] text-stone-500">{triggerType}</span>
              </div>
            </div>
          </div>

          {/* Status Toggle */}
          <div className="relative" onClick={handleToggle}>
            <div className={`w-10 h-5 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-stone-300'}`}>
              <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 py-3 border-t border-stone-100">
          <div className="flex items-center gap-2 text-stone-500">
            <FileText className="w-3.5 h-3.5" />
            <span className="text-xs">{stepCount} steps</span>
          </div>
          <div className="flex items-center gap-2 text-stone-500">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-xs">{formatLastEdited(workflow.updatedAt)}</span>
          </div>
        </div>
      </div>
    );
  }

  // Desktop view
  return (
    <div
      onClick={() => onCardClick(workflow)}
      className="bg-white border border-notion-border rounded-xl p-5 shadow-card hover:shadow-md transition-all cursor-pointer group"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500 border border-orange-100">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-notion-text group-hover:text-orange-600 transition">
              {workflow.name || 'Untitled Automation'}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge />
              <span className="text-[10px] text-stone-500">Trigger: {triggerType}</span>
            </div>
          </div>
        </div>

        {/* Status Toggle */}
        <div
          className="relative cursor-pointer"
          onClick={handleToggle}
          title={isActive ? 'Click to pause' : 'Click to activate'}
        >
          <div className={`w-10 h-5 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-stone-300'}`}>
            <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 py-4 border-t border-stone-100">
        <div className="text-center">
          <div className="text-sm font-bold text-stone-700">{stepCount}</div>
          <div className="text-[10px] text-stone-400 uppercase tracking-wide">Steps</div>
        </div>
        <div className="text-center border-l border-stone-100">
          <div className="text-sm font-bold text-stone-700">{leadCount}</div>
          <div className="text-[10px] text-stone-400 uppercase tracking-wide">Leads</div>
        </div>
        <div className="text-center border-l border-stone-100">
          <div className="text-sm font-bold text-stone-700">{completedCount}</div>
          <div className="text-[10px] text-stone-400 uppercase tracking-wide">Completed</div>
        </div>
      </div>

      <div className="flex justify-between items-center mt-2 pt-2">
        <span className="text-[10px] text-stone-400">Last edited {formatLastEdited(workflow.updatedAt)}</span>
        <div className="relative" ref={menuRef}>
          <button
            className="p-1.5 hover:bg-stone-100 rounded text-stone-400 hover:text-stone-600"
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {showMenu && (
            <div className="absolute right-0 bottom-full mb-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-50 min-w-[160px]">
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(false); onDeleteAutomation?.(workflow.id); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete Automation
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowCard;
